import React from 'react';
import { Link } from 'react-router-dom';
import { Award, TrendingUp, Info, ArrowRight } from 'lucide-react';
import { getPerformanceLabel, hasPerformanceScore } from '../client/VendorPerformanceBadge';

const PlatformPerformanceCard = ({ performance }) => {
 if (!performance) {
 return (
 <div className="rounded-lg border border-border/80 bg-surface shadow-sm p-5 sm:p-6">
 <div className="flex items-start gap-3">
 <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-surface-muted border border-border">
 <Award className="w-5 h-5 text-gray-400" />
 </div>
 <div className="min-w-0">
 <h3 className="text-sm font-semibold text-ink">
 Platform performance
 </h3>
 <p className="mt-1 text-sm text-ink-tertiary leading-relaxed">
 Complete platform bookings to build a score clients can see in Best Match.
 </p>
 <Link
 to="/vendor/analytics"
 className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-ink hover:text-ink"
 >
 View analytics
 <ArrowRight className="w-3.5 h-3.5" />
 </Link>
 </div>
 </div>
 </div>
 );
 }

 const score = Number(performance.score || 0);
 const bookings = Number(performance.platform_bookings_count || 0);
 const label = performance.label || getPerformanceLabel(score);
 const visible = performance.visible_to_clients ?? hasPerformanceScore(score, bookings);

 return (
 <div className="rounded-lg border border-border/80 bg-surface shadow-sm p-5 sm:p-6">
 <div className="flex items-start gap-4">
 <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-surface-muted border border-border">
 <span className="text-xl font-bold text-ink tabular-nums tracking-tight">
 {score > 0 ? score.toFixed(0) : '—'}
 </span>
 </div>
 <div className="min-w-0 flex-1">
 <div className="flex items-center gap-2 mb-1">
 <Award className="w-4 h-4 text-ink-tertiary" />
 <h3 className="text-sm font-semibold text-ink tracking-tight">
 Platform performance
 </h3>
 </div>
 <p className="text-sm text-ink-secondary leading-relaxed">
 Based on your last 90 days of platform bookings. Clients see this as{' '}
 <span className="font-semibold text-ink">{label || 'your score'}</span> in Best Match.
 </p>
 {visible ? (
 <p className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-100">
 <TrendingUp className="w-3 h-3" />
 Visible on your public profile
 </p>
 ) : (
 <p className="mt-2.5 text-xs text-ink-tertiary">
 Complete platform bookings to earn a public performance badge.
 </p>
 )}
 </div>
 </div>

 <div className="mt-5 grid grid-cols-3 gap-2.5">
 {[
 {
 label: 'Completion',
 value: `${Number(performance.completion_rate || 0).toFixed(0)}%`,
 },
 { label: 'Bookings', value: bookings },
 {
 label: 'Avg rating',
 value: Number(performance.average_rating || 0).toFixed(1),
 },
 ].map((stat) => (
 <div
 key={stat.label}
 className="rounded-xl bg-surface-muted/60 px-3 py-3"
 >
 <p className="text-[11px] font-medium uppercase tracking-wide text-ink-tertiary">
 {stat.label}
 </p>
 <p className="mt-1 text-lg font-bold text-ink tabular-nums">
 {stat.value}
 </p>
 </div>
 ))}
 </div>

 {performance.tips?.length > 0 && (
 <div className="mt-4 rounded-xl bg-surface-muted/60 px-4 py-3">
 <div className="flex items-start gap-2.5">
 <Info className="w-4 h-4 text-ink-tertiary mt-0.5 shrink-0" />
 <ul className="text-sm text-ink-secondary space-y-1 leading-relaxed">
 {performance.tips.slice(0, 2).map((tip) => (
 <li key={tip}>{tip}</li>
 ))}
 </ul>
 </div>
 </div>
 )}

 <Link
 to="/vendor/analytics"
 className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-ink hover:text-ink"
 >
 Full analytics
 <ArrowRight className="w-3.5 h-3.5" />
 </Link>
 </div>
 );
};

export default PlatformPerformanceCard;
